import React, { useState } from "react";
import { Typography, Link } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  StyledAttemptCard,
  StyledAttemptHeader,
  StyledAttemptBadge,
  StyledAttemptSubBlock,
  StyledTextMonospace,
  StyledFailureReasonBox,
  StyledPaymentSectionLabel,
  StyledPaymentSectionBlock,
  StyledPaymentFieldGrid,
  StyledPaymentFieldCell,
  StyledPaymentFieldLabel,
  StyledPaymentFieldValue,
  StyledProofImageWrapper,
  StyledProofImage,
  StyledZoomButton,
  StyledViewDocumentButton,
  StyledManualProofSection,
  PAYMENT_DETAIL_COLORS,
} from "./style";
import {
  formatPaymentDateTime,
  formatPaymentMethod,
  isManualPaymentRecord,
  shouldShowRazorpayOrderId,
} from "../../utils/paymentDisplay";

const NOTE_PREVIEW_LENGTH = 120;

function formatAttemptDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getAttemptBadgeColor(status, attemptStatusText) {
  if (status === "success") return "#00C046";
  if (status === "partial_success") return "#9c9c9c";
  if (status === "pending") return "#FF8500";
  if (status === "failed") {
    return attemptStatusText === "Cancelled" ? "#455a64" : "#FF3850";
  }
  if (status === "in_progress") return "#7B61FF";
  return "#9c9c9c";
}

function deriveSharedPaymentSummary(payments, formatCurrency) {
  const byCreated = [...payments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const getPayTime = (pay) => {
    const raw = pay.paid_at || pay.updatedAt || pay.createdAt;
    return raw ? new Date(raw).getTime() : 0;
  };
  const latestPay = [...payments].sort((a, b) => getPayTime(b) - getPayTime(a))[0];

  const amounts = payments.map((pay) => Number(pay.amount || 0));
  const allSameAmount = amounts.length > 0 && amounts.every((amount) => amount === amounts[0]);

  return {
    initiatedAt: formatAttemptDateTime(byCreated[0]?.createdAt),
    paymentDate: formatPaymentDateTime(latestPay),
    paymentAmount: allSameAmount
      ? formatCurrency(amounts[0])
      : formatCurrency(amounts.reduce((sum, amount) => sum + amount, 0)),
  };
}

function DetailFieldCell({ label, value, monospace = false, sx }) {
  return (
    <StyledPaymentFieldCell sx={sx}>
      <StyledPaymentFieldLabel sx={{ mb: 0.5, fontSize: "0.75rem" }}>
        {label}
      </StyledPaymentFieldLabel>
      {monospace ? (
        <StyledTextMonospace sx={{ wordBreak: "break-all", fontSize: "0.8125rem" }}>
          {value || "—"}
        </StyledTextMonospace>
      ) : (
        <StyledPaymentFieldValue sx={{ fontSize: "0.8125rem" }}>
          {value || "—"}
        </StyledPaymentFieldValue>
      )}
    </StyledPaymentFieldCell>
  );
}

function PaymentNoteField({ value }) {
  const [expanded, setExpanded] = useState(false);
  const note = String(value || "").trim();
  if (!note) return null;

  const isLong = note.length > NOTE_PREVIEW_LENGTH;
  const displayText = !isLong || expanded ? note : `${note.slice(0, NOTE_PREVIEW_LENGTH).trim()}…`;

  return (
    <StyledPaymentFieldCell sx={{ mt: 1 }}>
      <StyledPaymentFieldLabel sx={{ mb: 0.5, fontSize: "0.75rem" }}>
        Payment note
      </StyledPaymentFieldLabel>
      <StyledPaymentFieldValue
        sx={{
          fontSize: "0.8125rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.45,
        }}
      >
        {displayText}
      </StyledPaymentFieldValue>
      {isLong && (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            mt: 0.5,
            display: "inline-block",
            fontSize: "0.75rem",
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
          }}
        >
          {expanded ? "Show less" : "Read more"}
        </Link>
      )}
    </StyledPaymentFieldCell>
  );
}

function isImageProof(url) {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(url);
}

function PaymentProofBlock({ proofUrl }) {
  if (!proofUrl) return null;

  return (
    <StyledManualProofSection sx={{ mt: 1, mb: 0, maxWidth: 280 }}>
      <StyledPaymentFieldLabel sx={{ mb: 0.5, fontSize: "0.75rem" }}>
        Payment proof
      </StyledPaymentFieldLabel>
      {isImageProof(proofUrl) ? (
        <StyledProofImageWrapper>
          <StyledProofImage src={proofUrl} alt="Payment proof" />
          <StyledZoomButton
            component="a"
            href={proofUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            <ZoomInIcon sx={{ fontSize: 16, color: "#334155" }} />
          </StyledZoomButton>
        </StyledProofImageWrapper>
      ) : (
        <StyledViewDocumentButton
          component="a"
          href={proofUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
          startIcon={<InsertDriveFileIcon />}
        >
          View document
        </StyledViewDocumentButton>
      )}
    </StyledManualProofSection>
  );
}

function getAttemptInlineFields(pay) {
  const isManual = isManualPaymentRecord(pay);
  const paymentMode = formatPaymentMethod(pay.payment_method, pay);
  const fields = [{ label: "Payment mode", value: paymentMode }];

  if (!isManual) {
    fields.push({
      label: "Transaction ID",
      value: pay.razorpay_payment_id,
      monospace: true,
    });
    if (shouldShowRazorpayOrderId(pay)) {
      fields.push({
        label: "Razorpay order ID",
        value: pay.razorpay_order_id,
        monospace: true,
      });
    }
    return { inlineFields: fields, paymentNote: null };
  }

  if (pay.reference_number) {
    fields.push({
      label: "Reference number",
      value: pay.reference_number,
      monospace: true,
    });
  }

  return {
    inlineFields: fields,
    paymentNote: pay.payment_note?.trim() || null,
  };
}

function PaymentAttemptBlock({ pay, attemptNum, formatStatusText, showDivider }) {
  const attemptStatusText = formatStatusText(pay.status, pay.failure_reason);
  const badgeColor = getAttemptBadgeColor(pay.status, attemptStatusText);
  const { inlineFields, paymentNote } = getAttemptInlineFields(pay);

  return (
    <StyledAttemptSubBlock showDivider={showDivider}>
      <StyledAttemptHeader sx={{ mb: 1, pb: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color={PAYMENT_DETAIL_COLORS.fieldValue}
          sx={{ fontSize: "0.8125rem" }}
        >
          Attempt #{attemptNum}
          <Typography
            component="span"
            sx={{ fontWeight: 500, color: PAYMENT_DETAIL_COLORS.timelineTime, ml: 0.75 }}
          >
            ({pay.id.substring(0, 8)})
          </Typography>
        </Typography>
        <StyledAttemptBadge color={badgeColor}>{attemptStatusText}</StyledAttemptBadge>
      </StyledAttemptHeader>

      <StyledPaymentFieldGrid columns={Math.min(inlineFields.length, 3)}>
        {inlineFields.map((field) => (
          <DetailFieldCell
            key={field.label}
            label={field.label}
            value={field.value}
            monospace={field.monospace}
          />
        ))}
      </StyledPaymentFieldGrid>

      {paymentNote && <PaymentNoteField value={paymentNote} />}

      {isManualPaymentRecord(pay) && pay.payment_proof && (
        <PaymentProofBlock proofUrl={pay.payment_proof} />
      )}

      {pay.failure_reason && (
        <StyledFailureReasonBox sx={{ mt: 1, mb: 0, p: 1 }}>
          <Typography variant="caption" color="#d32f2f" fontWeight={600} display="block">
            Failure reason
          </Typography>
          <Typography variant="body2" color="#c62828" sx={{ fontSize: "0.8125rem" }}>
            {pay.failure_reason}
          </Typography>
        </StyledFailureReasonBox>
      )}
    </StyledAttemptSubBlock>
  );
}

export default function AdminPaymentCenterPanel({
  payments,
  invoiceId,
  formatCurrency,
  formatStatusText,
}) {
  const sharedSummary = deriveSharedPaymentSummary(payments, formatCurrency);
  const showInvoice = invoiceId && invoiceId !== "—";
  const summaryColumns = showInvoice ? 4 : 3;

  return (
    <StyledAttemptCard sx={{ p: "12px 14px" }}>
      <StyledPaymentSectionBlock sx={{ mb: 1 }}>
        <StyledPaymentSectionLabel sx={{ mb: 0.75 }}>
          Payment summary
        </StyledPaymentSectionLabel>
        <StyledPaymentFieldGrid columns={summaryColumns}>
          {showInvoice && (
            <DetailFieldCell label="Invoice ID" value={invoiceId} monospace />
          )}
          <DetailFieldCell label="Initiated at" value={sharedSummary.initiatedAt} />
          <DetailFieldCell label="Payment date" value={sharedSummary.paymentDate} />
          <DetailFieldCell label="Payment amount" value={sharedSummary.paymentAmount} />
        </StyledPaymentFieldGrid>
      </StyledPaymentSectionBlock>

      {payments.map((pay, index) => {
        const attemptsCount = payments.length;
        const attemptNum = attemptsCount - index;

        return (
          <PaymentAttemptBlock
            key={pay.id}
            pay={pay}
            attemptNum={attemptNum}
            formatStatusText={formatStatusText}
            showDivider={index > 0}
          />
        );
      })}
    </StyledAttemptCard>
  );
}
